// Calendar arithmetic for the date picker (ui/DateInput.tsx). Dates are calendar dates as
// YYYY-MM-DD strings, worked out in UTC so no time zone ever shifts a day.

const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export const MONTH_NAMES = MONTHS_LONG;
export const WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const pad = (n: number) => String(n).padStart(2, '0');
export const isoOf = (y: number, m: number, d: number) => `${y}-${pad(m + 1)}-${pad(d)}`;

/** Year, month (0-11) and day of an ISO date, or null if it is not a real date. */
export function partsOf(iso: string): { y: number; m: number; d: number } | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!match) return null;
  const y = Number(match[1]);
  const m = Number(match[2]) - 1;
  const d = Number(match[3]);
  const t = new Date(Date.UTC(y, m, d));
  return t.getUTCFullYear() === y && t.getUTCMonth() === m && t.getUTCDate() === d ? { y, m, d } : null;
}

/** The ISO date n days after (or before) another. */
export function addDaysIso(iso: string, n: number): string {
  const p = partsOf(iso);
  if (!p) return iso;
  const t = new Date(Date.UTC(p.y, p.m, p.d + n));
  return isoOf(t.getUTCFullYear(), t.getUTCMonth(), t.getUTCDate());
}

/**
 * How a change since a day reads on that day (APP-DESIGN.md 8.7): "today" on the day itself, "since
 * yesterday" for the day before, otherwise "since Nov 1" (with the year when it is another year's).
 */
export function sinceWords(from: string, today: string): string {
  if (from === today) return 'today';
  if (addDaysIso(from, 1) === today) return 'since yesterday';
  const p = partsOf(from);
  if (!p) return `since ${from}`;
  const short = `${MONTHS_LONG[p.m]!.slice(0, 3)} ${p.d}`;
  return `since ${from.slice(0, 4) === today.slice(0, 4) ? short : `${short}, ${p.y}`}`;
}

/** The same day n months later, clamped to the month's end (Jan 31 + 1 month = Feb 28). */
export function addMonthsIso(iso: string, n: number): string {
  const p = partsOf(iso);
  if (!p) return iso;
  const first = new Date(Date.UTC(p.y, p.m + n, 1));
  const last = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate();
  return isoOf(first.getUTCFullYear(), first.getUTCMonth(), Math.min(p.d, last));
}

/** The 42 days (six weeks, Sunday first) that show a month in the calendar. */
export function monthGrid(y: number, m: number): string[] {
  const first = new Date(Date.UTC(y, m, 1));
  const start = new Date(Date.UTC(y, m, 1 - first.getUTCDay()));
  return Array.from({ length: 42 }, (_, i) => {
    const t = new Date(start.getTime() + i * 86_400_000);
    return isoOf(t.getUTCFullYear(), t.getUTCMonth(), t.getUTCDate());
  });
}

/** "Oct 17, 2026" for the input, "Friday, October 17, 2026" for screen readers. */
export function longDate(iso: string): string {
  const p = partsOf(iso);
  if (!p) return iso;
  const weekday = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][new Date(Date.UTC(p.y, p.m, p.d)).getUTCDay()];
  return `${weekday}, ${MONTHS_LONG[p.m]} ${p.d}, ${p.y}`;
}

/**
 * A typed date as ISO, or null. Reads 2026-10-17, 10/17/2026 and 10/17/26 (month first, as in the
 * US), and month names either way round: Oct 17 2026, 17 October 2026.
 */
export function parseDateText(text: string): string | null {
  const t = text.trim().toLowerCase().replace(/,/g, ' ').replace(/\s+/g, ' ');
  if (!t) return null;
  const real = (y: number, m: number, d: number) => {
    const iso = isoOf(y, m, d);
    return partsOf(iso) ? iso : null;
  };
  let match = /^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})$/.exec(t);
  if (match) return real(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  match = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{2}|\d{4})$/.exec(t);
  if (match) {
    const year = Number(match[3]);
    return real(year < 100 ? 2000 + year : year, Number(match[1]) - 1, Number(match[2]));
  }
  const month = (word: string) => MONTHS_LONG.findIndex((m) => m.toLowerCase().startsWith(word) && word.length >= 3);
  match = /^([a-z]+)\.? (\d{1,2})(?:st|nd|rd|th)? (\d{4})$/.exec(t);
  if (match && month(match[1]!) >= 0) return real(Number(match[3]), month(match[1]!), Number(match[2]));
  match = /^(\d{1,2})(?:st|nd|rd|th)? ([a-z]+)\.? (\d{4})$/.exec(t);
  if (match && month(match[2]!) >= 0) return real(Number(match[3]), month(match[2]!), Number(match[1]));
  return null;
}
